import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from "rxjs";
import { filter, map, switchMap, switchMapTo, take, tap } from "rxjs/operators";
import { ExternalCatalogLoader } from "./external-catalog-loader.service";
import { SecoWindowService } from "./seco-window.service";

const log = (...msgs) => {
  console.log('[@uxdf/seco-piloting]', ...msgs);
};

@Injectable()
export class RxExternalCatalog {
  /**
   * The ExternalCatalog object instance
   * @see {@link https://rndwww.nce.amadeus.net/static/jmarengo/Smart%20Scripting%20documentation%2020.6%20Central/getting-started/smartscriptingguide-gettingstarted.html#including-the-externalcatalog}
   */
  private catalog = null;

  /**
   * A behaviour subject which contains the logged in status of a user in seco.
   */
  public loggedIn$ = new BehaviorSubject<boolean>(false);

  /**
   * A behaviour subject which contains the connected to seco status of the api.
   */
  public connected$ = new BehaviorSubject<boolean>(false);

  /**
   * A behaviour subject which contains the loading status of the ExternalCatalog.js script.
   */
  public catalogLoaded$ = new BehaviorSubject<boolean>(false);

  /**
   * An observable which emits true when the following conditions are fulfilled:
   * - ExternalCatalog.js was successfully loaded
   * - Seco was successfully connected
   * - The user is logged in to seco
   *
   * Also it ensures that when it emits a seco window is open.
   */
  public catalogReady$: Observable<boolean> = this.catalogLoaded$.pipe(
    filter(loaded => loaded),
    switchMap(() => this.secoWindow.openOrFocus()),
    switchMap(() => combineLatest([this.connected$, this.loggedIn$])),
    map((loaded: boolean[]) => loaded.every((l) => l))
  );

  constructor(private loader: ExternalCatalogLoader, private secoWindow: SecoWindowService){
    /**
     * Load ExternalCatalog.js and setup connect listener.
     * Todo: consider retry when catalog rejects
     */
    this.loader.loadCatalog().then((catalog) => {
      this.catalog = catalog;
      this.catalogLoaded$.next(true);
      this._connect();
    }).catch((err) => {
      console.warn('Error loading ExternalCatalog', err);
    });

    /**
     * When ExternalCatalog.js was loaded and seco is connected, subscribe to sessionStatus events in seco to maintain
     * loggedIn$ when session expires.
     * Todo: Check if we can subscribe to catalogReady$ as well
     */
    this.catalogLoaded$.pipe(
      filter(loaded => loaded),
      switchMap(() => this.connected$),
      filter(connected => connected),
      take(1)
    ).subscribe(() => {
      this.event('session.statusChanged').subscribe((res) => {
        this.loggedIn$.next(res.statusChange.toLowerCase() === 'unlock');
      });
    });

    this.secoWindow.windowIsOpen$.subscribe((isOpen) => {
      this.connected$.next(false);
    })
  }

  /**
   * Sets the context to the given task id.
   * * @see {@link https://rndwww.nce.amadeus.net/static/jmarengo/Smart%20Scripting%20documentation%2020.6%20Central/other-methods/smartscriptingguide-othermethods.html#setContext}
   */
  setContext(ctx: string): Observable<any> {
    return this.catalogReady$.pipe(
      tap((val) => { log('catalogReady$', val) }),
      filter((loggedIn) => loggedIn),
      take(1),
      tap(() => this.catalog.context = ctx)
    );
  }

  /**
   * Subscribe to an event. Ensures catalog is ready by using catalogReady$. For available events see doc.
   * @see {@link https://rndwww.nce.amadeus.net/static/jmarengo/Smart%20Scripting%20documentation%2020.6%20Central/events/index.html}
   */
  event(eventName: string): Observable<any> {
    return this.catalogReady$.pipe(
      tap((val) => { log('catalogReady$', val) }),
      filter((loggedIn) => loggedIn),
      take(1),
      switchMapTo(this._event(eventName))
    );
  }

  /**
   * Registers to a service. Ensures catalog is ready by using catalogReady$. For available services see doc.
   * @see {@link https://rndwww.nce.amadeus.net/static/jmarengo/Smart%20Scripting%20documentation%2020.6%20Central/services/index.html}
   */
  requestService(serviceName: string, params = null): Observable<any> {
    return this.catalogReady$.pipe(
      tap((val) => { log('catalogReady$', val) }),
      filter((loggedIn) => loggedIn),
      take(1),
      switchMap(() => this._requestService(serviceName, params))
    );
  }

  /**
   * Internal implementation of the event subscription against the ExternalCatalog api. Wraps provided callback-based
   * api in an observable.
   */
  private _event(eventName: string): Observable<any> {
    return Observable.create((observer) => {
      log('catalog.subscribe', eventName);
      const ctx = this.catalog.getContext();
      const callback = (res) => {
        log('catalog.subscribe.callback', eventName, res);
        observer.next(res);
      };
      const unsubscribe = () => {
        log('catalog.unsubscribe', eventName);
        this.catalog.unsubscribe(eventName, ctx, {
          fn: () => {
            log('catalog.unsubscribe.callback', eventName);
          },
          scope: this
        })
      };

      this.catalog.subscribe(eventName, {
        fn: callback,
        scope: this
      });
      // adds a function to be called when the observable got unsubscribed.
      observer.add(unsubscribe);
    });
  }

  /**
   * Sets up a callback for the connect event fired by seco once loaded.
   */
  private _connect() {
    log('catalog.connect');
    this.catalog.connect({
      scope: this,
      fn: {
        onSuccess: (res) => {
          log('catalog.connect.onSuccess', res);
          this.loggedIn$.next(true);
          this.connected$.next(true);
        },
        onError: (e) => {
          log('catalog.connect.onError', e);
          this.connected$.error(e);
        },
      },
    });
  }

  /**
   * Internal implementation of requestService. Wraps provided callback-based api in a Promise.
   * @param serviceName
   * @param params
   */
  private _requestService(serviceName: string, params = null): Promise<any> {
    return new Promise((resolve, reject) => {
      log(`this.catalog.${serviceName}`, params);
      const opts = {
        fn: {
          onError: function (e) {
            log(`this.catalog.${serviceName}.onError`, e);
            reject(e);
          },
          onSuccess: function (res, cb_params) {
            log(`this.catalog.${serviceName}.onSuccess`, res);
            resolve({ res, cb_params });
          }
        }
      };

      this.catalog.requestService(serviceName, params, opts);
    });
  }

}
